import { useFetcher, useLoaderData } from "@remix-run/react"
import clsx from "clsx"
import { useEffect, useState, type ComponentProps } from "react"
import { type Theme } from "#app/utils/theme.server"
import { Button } from "./ui/button"
import { Icon } from "./ui/icon"

export function ThemeSwitch({ className }: ComponentProps<"div">) {
	const fetcher = useFetcher<{
		success: boolean
		submission: { value: { theme: Theme } }
	}>()
	const loaderData = useLoaderData<{
		requestInfo: { userPrefs: { theme: Theme | "system" } }
	}>()
	const [mode, setMode] = useState<Theme | "system">(
		loaderData?.requestInfo?.userPrefs?.theme,
	)

	useEffect(() => {
		if (fetcher.state !== "loading" && !fetcher.data) {
			fetcher.load("/switch-theme")
		}
	}, [fetcher, fetcher.data, fetcher.state])

	useEffect(() => {
		if (fetcher.data && fetcher.data.submission?.value?.theme) {
			setMode(fetcher.data.submission.value.theme)
		} else {
			setMode("system")
		}
	}, [fetcher.data])

	const toggleMode = () => {
		let nextMode: Theme | "system"
		// Determine the next mode based on the current mode
		switch (mode) {
			case "system":
				nextMode = "light"
				break
			case "light":
				nextMode = "dark"
				break
			case "dark":
				nextMode = "system"
				break
			default:
				nextMode = "system"
				break
		}

		// Update the mode immediately for UI to reflect the change
		setMode(nextMode)

		// Submit the new mode to the server
		fetcher.submit(
			{ theme: nextMode },
			{ method: "post", action: "/switch-theme" },
		)
	}

	const modeLabel: { [key in Theme | "system"]: JSX.Element } = {
		light: (
			<Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>
		),
		dark: (
			<Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>
		),
		system: (
			<Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>
		),
	}

	const classNames = clsx("px-4", className)

	return (
		<div className="flex gap-2">
			<fetcher.Form
				action="/switch-theme"
				method="POST"
				onSubmit={e => {
					e.preventDefault() // Prevent form from submitting traditionally
					toggleMode()
				}}
			>
				<input type="hidden" name="theme" value={mode} />
				<Button
					variant="default"
					className={classNames}
					size="icon"
					type="submit"
				>
					{modeLabel[mode]}
				</Button>
			</fetcher.Form>
		</div>
	)
}
